import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  Stack,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  Chip,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { hostApiClient } from '../../hostApiClient';
import { Space, SpaceScheduleWeeklyItem, SpaceWeeklySchedule, UpdateSpaceRequest } from '../../models/Space';
import { SpaceAmenity } from '../../models/SpaceAmenity';
import { SpaceGear } from '../../models/SpaceGear';
import { HostRoom } from '../../models/Room';
import { OpenInNew } from '@mui/icons-material';
import MediaUploader from '../../components/MediaUploader';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import AmountInput from '../../components/AmountInput';
import { useTranslation } from 'react-i18next';
import { Autocomplete as GooglePlacesAutocomplete } from '@react-google-maps/api';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { scrollToElementByIdWithRetries } from '../../utils/scrollToElement';
import { TUTOR_WORKING_HOURS_SECTION_ID } from '../../utils/tutorWorkingHours';
import { countryInfo } from '../../countries';
import { NotificationSnackbar, SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import { authService } from '../../services/AuthService';
import { schoolTutorPagePath } from '../../utils/publicSpaceResolution';
import { OrganizationType } from '../../models/Organization';
import { apiClient, HOST_RETENTION_EVENTS } from '../../publicApiClient';
import RoomPublicationManager, {
  orderHostRooms,
  type RoomPublicationManagerHandle,
} from '../../components/host/RoomPublicationManager';

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  PLN: 'zł',
  GBP: '£',
  AUD: 'A$',
};

const SpaceDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [space, setSpace] = useState<Space | null>(null);
  const [gears, setGears] = useState<SpaceGear[]>([]);
  const [newGear, setNewGear] = useState<SpaceGear>({ gearType: '', details: '', pricePerH: 0 });
  const [amenities, setAmenities] = useState<SpaceAmenity[] | []>([]);
  const [newAmenity, setNewAmenity] = useState<SpaceAmenity>({ amenityType: '', details: '', pricePerH: 0 });
  const [editValues, setEditValues] = useState<UpdateSpaceRequest | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [publicationDirty, setPublicationDirty] = useState(false);
  const [rooms, setRooms] = useState<HostRoom[]>([]);
  const [isIndividualTutorOrg, setIsIndividualTutorOrg] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const navigate = useNavigate();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const roomPublicationRef = useRef<RoomPublicationManagerHandle>(null);
  const canSave = hasChanges || publicationDirty;
  const handlePublicationDirtyChange = useCallback((dirty: boolean) => {
    setPublicationDirty(dirty);
  }, []);
  const { isLoaded, loadError } = useGoogleMaps();
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const fetchRoomsForSpace = async () => {
      if (!id) return;
      try {
        const organization = await hostApiClient.getOrganization().catch(() => null);
        const individualTutor = organization?.organizationType === OrganizationType.INDIVIDUAL_TUTOR;
        setIsIndividualTutorOrg(individualTutor);
        if (individualTutor) {
          setRooms([]);
          return;
        }
        const allRooms = await hostApiClient.getRooms(id);
        setRooms(orderHostRooms(allRooms));
      } catch (err) {
        console.error(t('spaceDetails.failedToFetchRooms'), err);
      }
    };
  
    fetchRoomsForSpace();
  }, [id, t]);

  useEffect(() => {
    if (!editValues?.weeklySchedule) return;
    if (location.hash !== `#${TUTOR_WORKING_HOURS_SECTION_ID}`) return;
    return scrollToElementByIdWithRetries(TUTOR_WORKING_HOURS_SECTION_ID, {
      offsetPx: 72,
      delaysMs: [80, 250, 600, 1200],
    });
  }, [editValues?.weeklySchedule, location.hash]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchSpace = async () => {
      try {
        const data = await hostApiClient.getSpace(id);
        console.log('Fetched space data:', data);

        setSpace(data.space);
        setAmenities(data.amenities);
        setGears(data.gears);
        const editData: UpdateSpaceRequest = {
          name: data.space.name,
          description: data.space.description,
          imageUrls: data.space.imageUrls,
          howToFindVideoUrls: data.space.howToFindVideoUrls,
          googleMapsUrl: data.space.googleMapsUrl,
          googlePlaceId: data.space.googlePlaceId,
          addressLine1: data.space.addressLine1,
          addressLine2: data.space.addressLine2,
          city: data.space.city,
          state: data.space.state,
          zipCode: data.space.zipCode,
          countryCode: data.space.countryCode,
          weeklySchedule: data.space.weeklySchedule,
          amenities: data.amenities,
          gears: data.gears,
          currencyCode: data.space.currencyCode,
          latitude: data.space.latitude,
          longitude: data.space.longitude,
        };

        console.log('Setting edit values:', editData);
        setEditValues(editData);
      } catch (err) {
        console.error(t('spaceDetails.failedToFetchSpace'), err);
      }
    };

    fetchSpace();
  }, [id, t]);

  // Hydrate Google Place for map + address input (single fetch — do not duplicate)
  useEffect(() => {
    if (editValues?.googlePlaceId && isLoaded && window.google) {
      const service = new google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId: editValues.googlePlaceId,
          fields: ['address_components', 'geometry', 'formatted_address', 'url', 'place_id', 'name']
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            setSelectedPlace(place);
            if (place.geometry?.location) {
              setMapCenter({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              });
            }
            if (inputRef.current) {
              inputRef.current.value = place.name || place.formatted_address || '';
            }
          } else {
            console.error('Failed to fetch place details:', status);
          }
        }
      );
    }
  }, [editValues?.googlePlaceId, isLoaded]);

  const handleAddAmenity = () => {
    if (!newAmenity.amenityType.trim()) return;
  
    const updatedAmenities = [...amenities, { ...newAmenity, spaceId: id, details: newAmenity.details?.trim() || '' }];
    setAmenities(updatedAmenities);
    setEditValues((prev) => prev ? { ...prev, amenities: updatedAmenities } : prev);
    setNewAmenity({ amenityType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleAddGear = () => {
    if (!newGear.gearType.trim()) return;
  
    const updatedGears = [...gears, { ...newGear, details: newGear.details?.trim() || '' }];
    setGears(updatedGears);
    setEditValues((prev) => prev ? { ...prev, gears: updatedGears } : prev);
    setNewGear({ gearType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleDeleteGear = (index: number) => {
    const updatedGears = gears.filter((_, i) => i !== index);
    setGears(updatedGears);
    setEditValues((prev) => prev ? { ...prev, gears: updatedGears } : prev);
    setHasChanges(true);
  };
  
  const handleDeleteAmenity = (index: number) => {
    const updatedAmenities = amenities.filter((_, i) => i !== index);
    setAmenities(updatedAmenities);
    setEditValues((prev) => prev ? { ...prev, amenities: updatedAmenities } : prev);
    setHasChanges(true);
  };

  const handleChange = useCallback((field: keyof UpdateSpaceRequest, value: any) => {
    if (!editValues) return;
    setEditValues({ ...editValues, [field]: value });
    setHasChanges(true);
  }, [editValues]);

  const handleScheduleChange = (
    day: keyof SpaceWeeklySchedule,
    field: keyof SpaceScheduleWeeklyItem,
    value: any
  ) => {
    if (!editValues || !editValues.weeklySchedule) return;
  
    const newSchedule: SpaceWeeklySchedule = { ...editValues.weeklySchedule };
    const updatedDay = {
      ...newSchedule[day],
      [field]: typeof value === 'boolean' ? value : value.target?.value || value,
    };
  
    if (field === 'openAllDay' && value === true) {
      updatedDay.dayOff = false;
      updatedDay.periodStart = '';
      updatedDay.periodEnd = '';
    }
  
    if (field === 'dayOff' && value === true) {
      updatedDay.openAllDay = false;
      updatedDay.periodStart = '';
      updatedDay.periodEnd = '';
    }
  
    newSchedule[day] = updatedDay;
    setEditValues({ ...editValues, weeklySchedule: newSchedule });
    setHasChanges(true);
  };  

  const renderTimeCell = (field: 'periodStart' | 'periodEnd') => (params: GridRenderCellParams) => {
    const { openAllDay, dayOff } = params.row;
    const value = params.row[field];
    const timeValue = value ? dayjs(`1970-01-01T${value}`) : null;

    const handleTimeChange = (newValue: Dayjs | null) => {
      if (!newValue) return;
      const formatted = newValue.format('HH:mm');
      handleScheduleChange(params.row.day, field, formatted);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={handleTimeChange}
          minutesStep={30}
          disabled={openAllDay || dayOff}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              sx: { mt: 1 },
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const scheduleColumns: GridColDef[] = [
    { field: 'dayTranslated', headerName: t('space.day'), flex: 1 },
    {
      field: 'periodStart',
      headerName: t('space.open'),
      flex: 1,
      renderCell: renderTimeCell('periodStart'),
    },
    {
      field: 'periodEnd',
      headerName: t('space.close'),
      flex: 1,
      renderCell: renderTimeCell('periodEnd'),
    },
    {
      field: 'openAllDay',
      headerName: t('space.24hOpen'),
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={(e) => handleScheduleChange(params.row.day, 'openAllDay', e.target.checked)}
        />
      ),
    },
    {
      field: 'dayOff',
      headerName: t('space.dayOff'),
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={(e) => handleScheduleChange(params.row.day, 'dayOff', e.target.checked)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    if (!id) return;

    if (hasChanges) {
      if (!editValues?.name || !editValues.googleMapsUrl || !editValues?.googleMapsUrl.startsWith('http')) {
        setSnackbarMessage(t('space.validGoogleMapsUrlError', 'Please enter a valid Google Maps URL.'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (!editValues) return;
    }

    // Save space and room publication independently so one failure does not block the other.
    const shouldSaveSpace = hasChanges && !!editValues;
    const shouldSavePublication = publicationDirty;
    let spaceError: unknown = null;
    let publicationError: unknown = null;

    if (shouldSaveSpace && editValues) {
      try {
        await hostApiClient.updateSpace(id, editValues);
        setSpace(space);
        setEditValues(editValues);
        setHasChanges(false);
      } catch (err) {
        spaceError = err;
        console.error(t('spaceDetails.failedToUpdateSpace'), err);
      }
    }

    if (shouldSavePublication) {
      try {
        await roomPublicationRef.current?.save();
      } catch (err) {
        publicationError = err;
        console.error(t('rooms.publicationSaveFailed', 'Could not save room order.'), err);
      }
    }

    if (!spaceError && !publicationError) {
      setSnackbarMessage(t('common.changesSaved', 'Changes saved successfully'));
      setSnackbarSeverity('success');
    } else if (spaceError && shouldSavePublication && !publicationError) {
      setSnackbarMessage(
        t(
          'spaceDetails.spaceSaveFailedRoomsSaved',
          'Space details could not be saved, but room order was saved.',
        ),
      );
      setSnackbarSeverity('warning');
    } else if (!spaceError && publicationError) {
      setSnackbarMessage(t('rooms.publicationSaveFailed', 'Could not save room order.'));
      setSnackbarSeverity('error');
    } else {
      setSnackbarMessage(t('common.saveError', 'Failed to save changes'));
      setSnackbarSeverity('error');
    }
    setSnackbarOpen(true);
  };

  const handleCancel = () => {
    if (hasChanges) {
      setEditValues(editValues);
      setSpace(space);
      setGears(gears);
      setAmenities(amenities);
      setHasChanges(false);
    }
    if (publicationDirty) {
      roomPublicationRef.current?.discard();
    }
    setSnackbarMessage(t('common.changesCancelled', 'Changes cancelled'));
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onPlaceSelected = useCallback((place: google.maps.places.PlaceResult) => {
    setSelectedPlace(place);
    
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMapCenter({ lat, lng });
      
      // Update form values
      if (editValues) {
        const newEditValues = { ...editValues };
        newEditValues.googleMapsUrl = place.url || '';
        newEditValues.googlePlaceId = place.place_id || '';
        newEditValues.latitude = lat;
        newEditValues.longitude = lng;
        
        // Initialize address components
        let streetNumber = '';
        let route = '';
        let countryCode = '';
        
        // Extract address components
        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes('locality')) {
            newEditValues.city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            newEditValues.state = component.long_name;
          } else if (types.includes('postal_code')) {
            newEditValues.zipCode = component.long_name;
          } else if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('country')) {
            countryCode = component.short_name;
            newEditValues.countryCode = countryCode;
          }
        });

        // Combine route and street number (route first)
        newEditValues.addressLine1 = [route, streetNumber].filter(Boolean).join(' ');
        
        // Get country info for currency
        const country = countryInfo[countryCode] || { currency: 'USD' };
        newEditValues.currencyCode = country.currency;
        
        // Force a new object to ensure React detects the change
        setEditValues({ ...newEditValues });
        setHasChanges(true);
      }
    }
  }, [editValues, setEditValues, setHasChanges]);

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'geometry', 'formatted_address', 'url', 'place_id', 'name']);

    // Place details are loaded once in the googlePlaceId effect — only restore the input label here.
    if (selectedPlace && inputRef.current) {
      inputRef.current.value = selectedPlace.name || selectedPlace.formatted_address || '';
    }
  }, [selectedPlace]);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      setHasInteracted(true);
      onPlaceSelected(place);
    }
  }, [onPlaceSelected]);

  const publicSchoolUrl =
    editValues?.city && editValues?.name
      ? `${window.location.origin}${schoolTutorPagePath(editValues.city, editValues.name)}`
      : null;

  const copyPublicSchoolUrl = async () => {
    if (!publicSchoolUrl) return;
    try {
      await navigator.clipboard.writeText(publicSchoolUrl);
      void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.PUBLIC_SCHOOL_LINK_COPIED, {
        spaceId: id,
        spaceName: editValues?.name,
        publicUrl: publicSchoolUrl,
      });
      setSnackbarMessage(t('space.publicSchoolLinkCopied', 'Public school page link copied.'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (_error) {
      setSnackbarMessage(t('space.publicSchoolLinkCopyError', 'Could not copy public school page link.'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const openPublicSchoolUrl = () => {
    if (!publicSchoolUrl) return;
    void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.PUBLIC_SCHOOL_LINK_OPENED, {
      spaceId: id,
      spaceName: editValues?.name,
      publicUrl: publicSchoolUrl,
    });
    window.open(publicSchoolUrl, '_blank', 'noopener,noreferrer');
  };

  if (loadError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="error">{t('common.errorLoadingMaps')}</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto'}}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {editValues?.name}
          </Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('spaces.name')}
                  value={editValues?.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  fullWidth
                />
              </Grid>

              {publicSchoolUrl ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderRadius: 1,
                      position: 'relative',
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.87)',
                      },
                      '&:focus-within': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        left: 8,
                        backgroundColor: 'background.paper',
                        px: 1,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {t('space.publicSchoolLink', 'Public school page')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        minHeight: 56,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflowWrap: 'anywhere',
                          maxWidth: { xs: '100%', sm: '70%' },
                          fontFamily: 'monospace',
                          color: 'text.primary',
                        }}
                      >
                        {publicSchoolUrl}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNew fontSize="small" />}
                          onClick={openPublicSchoolUrl}
                        >
                          {t('common.open', 'Open')}
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => void copyPublicSchoolUrl()}>
                          {t('common.copy', 'Copy')}
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Grid>
              ) : null}

              {/* Space Join Link - Only for admins */}
              <Grid item xs={12}>
                {(() => {
                  const userRoles = authService.getRoles();
                  console.log('Current user roles:', userRoles);
                  const isAdmin = userRoles.includes('ADMIN');
                  console.log('Is admin:', isAdmin);
                  
                  if (!isAdmin || !space?.joinCode) {
                    console.log('Not showing Space Join Link - isAdmin:', isAdmin, 'hasJoinCode:', !!space?.joinCode);
                    return null;
                  }

                  const joinLink = `${window.location.origin}/join/${space.joinCode}`;
                  console.log('Showing Space Join Link for space:', space.name, 'joinCode:', space.joinCode, 'link:', joinLink);

                  return (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderRadius: 1,
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&:focus-within': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          left: 8,
                          backgroundColor: 'background.paper',
                          px: 1,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        {t('space.joinLink', 'Space Join Link')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.5,
                          minHeight: 56,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            overflowWrap: 'anywhere',
                            maxWidth: '80%',
                            fontFamily: 'monospace',
                            color: 'text.primary',
                          }}
                        >
                          {joinLink}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            navigator.clipboard.writeText(joinLink);
                            setSnackbarMessage(t('common.linkCopied', 'Link copied to clipboard'));
                            setSnackbarSeverity('success');
                            setSnackbarOpen(true);
                          }}
                        >
                          {t('common.copy', 'Copy')}
                        </Button>
                      </Box>
                    </Box>
                  );
                })()}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t('spaces.description')}
                  value={editValues?.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={10}
                />
              </Grid>

              <Grid item xs={12}>
                <GooglePlacesAutocomplete
                  onLoad={onLoad}
                  onPlaceChanged={onPlaceChanged}
                  restrictions={{ country: 'pl' }}
                >
                  <TextField
                    inputRef={inputRef}
                    label={t('space.placeLocation')}
                    fullWidth
                    placeholder={t('space.placeLocationPlaceholder', 'Search for a location in Poland')}
                    required
                    error={hasInteracted && !selectedPlace}
                    helperText={hasInteracted && !selectedPlace ? t('space.placeLocationRequired', 'Please select a location from Google Maps') : ' '}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </GooglePlacesAutocomplete>

                <Box sx={{ mt: 2, height: { xs: 200, sm: 300 }, width: '100%' }}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={15}
                  >
                    {selectedPlace?.geometry?.location && (
                      <Marker
                        position={{
                          lat: selectedPlace.geometry.location.lat(),
                          lng: selectedPlace.geometry.location.lng(),
                        }}
                      />
                    )}
                  </GoogleMap>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('space.city')}
                  value={editValues?.city ?? ''}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('space.state')}
                  value={editValues?.state ?? ''}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('space.zipCode')}
                  value={editValues?.zipCode ?? ''}
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('space.addressLine1')}
                  value={editValues?.addressLine1 ?? ''}
                  onChange={(e) => handleChange('addressLine1', e.target.value)}
                  fullWidth
                  required
                  error={hasInteracted && !editValues?.addressLine1?.trim()}
                  helperText={hasInteracted && !editValues?.addressLine1?.trim() ? t('space.addressLine1Required', 'Address Line 1 is required') : ' '}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('space.addressLine2')}
                  value={editValues?.addressLine2 ?? ''}
                  onChange={(e) => handleChange('addressLine2', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t('space.googleMapsURL')}
                  value={editValues?.googleMapsUrl ?? ''}
                  fullWidth
                  required
                  type="url"
                  onChange={(e) => handleChange('googleMapsUrl', e.target.value)}
                  InputProps={{
                    endAdornment: editValues?.googleMapsUrl && (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => window.open(editValues?.googleMapsUrl, '_blank')}
                          size="small"
                        >
                          <OpenInNew />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <MediaUploader
                  label={t('space.spaceImages')}
                  initialMedia={editValues?.imageUrls || []}
                  onMediaChange={(newMedia) => {
                    const current = editValues?.imageUrls || [];
                    if (JSON.stringify(current) === JSON.stringify(newMedia)) return;
                    handleChange('imageUrls', newMedia);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <MediaUploader
                  label={t('space.howToFindVideo', 'How to Find the Space (Video/Image)')}
                  initialMedia={(editValues?.howToFindVideoUrls || []) as string[]}
                  onMediaChange={(newMedia) => {
                    const current = (editValues?.howToFindVideoUrls || []) as string[];
                    if (JSON.stringify(current) === JSON.stringify(newMedia)) return;
                    handleChange('howToFindVideoUrls', newMedia);
                  }}
                  maxFiles={1}
                />
              </Grid>

              {editValues?.weeklySchedule && (
                <Grid
                  item
                  xs={12}
                  id={TUTOR_WORKING_HOURS_SECTION_ID}
                  sx={{ scrollMarginTop: 72 }}
                >
                  <Typography variant="h6" gutterBottom mt={2}>
                    {t('space.weeklySchedule')}
                  </Typography>

                  {isMobile ? (
                    <Stack spacing={2} mt={2} mb={2}>
                      {Object.entries(editValues.weeklySchedule).map(([day, schedule]) => {
                        const dayLabel = t(`space.days.${String(day).toLowerCase()}`);
                        const openAllDay = (schedule as any).openAllDay;
                        const dayOff = (schedule as any).dayOff;
                        const periodStart = (schedule as any).periodStart;
                        const periodEnd = (schedule as any).periodEnd;
                        const timeStart = periodStart ? dayjs(`1970-01-01T${periodStart}`) : null;
                        const timeEnd = periodEnd ? dayjs(`1970-01-01T${periodEnd}`) : null;

                        return (
                          <Box key={day} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {dayLabel}
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label={t('space.open')}
                                    value={timeStart}
                                    onChange={(newValue) => {
                                      if (!newValue) return;
                                      const formatted = newValue.format('HH:mm');
                                      handleScheduleChange(day as keyof SpaceWeeklySchedule, 'periodStart', formatted);
                                    }}
                                    minutesStep={30}
                                    disabled={openAllDay || dayOff}
                                    ampm={false}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label={t('space.close')}
                                    value={timeEnd}
                                    onChange={(newValue) => {
                                      if (!newValue) return;
                                      const formatted = newValue.format('HH:mm');
                                      handleScheduleChange(day as keyof SpaceWeeklySchedule, 'periodEnd', formatted);
                                    }}
                                    minutesStep={30}
                                    disabled={openAllDay || dayOff}
                                    ampm={false}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={openAllDay}
                                        onChange={(e) => handleScheduleChange(day as keyof SpaceWeeklySchedule, 'openAllDay', e.target.checked)}
                                      />
                                    }
                                    label={t('space.24hOpen')}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={dayOff}
                                        onChange={(e) => handleScheduleChange(day as keyof SpaceWeeklySchedule, 'dayOff', e.target.checked)}
                                      />
                                    }
                                    label={t('space.dayOff')}
                                  />
                                </Grid>
                              </Grid>
                            </LocalizationProvider>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Grid item xs={12} mt={2} mb={2}>
                      <DataGrid
                        autoHeight
                        hideFooter
                        disableRowSelectionOnClick
                        rows={Object.entries(editValues.weeklySchedule).map(
                          ([day, schedule], idx) => ({
                            id: idx,
                            day,
                            dayTranslated: t(`space.days.${day.toLowerCase()}`),
                            ...schedule,
                          })
                        )}
                        columns={scheduleColumns}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            <Stack direction="column" spacing={4} mt={4} mb={4}>
              {/* Gears Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('space.gears')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                  <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
                    <Select
                      value={newGear.gearType}
                      onChange={(e) => setNewGear({ ...newGear, gearType: e.target.value })}
                    >
                      {Object.keys(gearIcons).map((type) => (
                        <MenuItem key={type} value={type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {gearIcons[type]}
                            <span>{getGearLabel(type)}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('space.details')}
                    size="small"
                    value={newGear.details}
                    onChange={(e) => setNewGear({ ...newGear, details: e.target.value })}
                    sx={{ width: { xs: '100%', md: 200 } }}
                  />

                  <AmountInput
                    amount={newGear.pricePerH.toString()}
                    label={t('space.pricePerHour')}
                    onAmountChange={(newAmount) => setNewGear({ ...newGear, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[editValues?.currencyCode.toString() as keyof typeof currencySymbols] || '$'}
                    responsiveWidth={{ xs: '100%', md: 120 }}
                  />

                  <Tooltip title={t('space.addGear')}>
                    <IconButton color="primary" onClick={handleAddGear} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Gears Chip list — wraps under the inputs but aligned to the left */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center', width: '100%' }}>
                    {gears.map((gear, index) => (
                      <Chip
                        key={index}
                        label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}${gear.pricePerH ? ` - ${gear.pricePerH.toFixed(2)} ${editValues?.currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteGear(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>


              {/* Amenities Section */}
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('space.amenities')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                  <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
                    <Select 
                      value={newAmenity.amenityType}
                      onChange={(e) => setNewAmenity({ ...newAmenity, amenityType: e.target.value })}
                    >
                      {Object.keys(amenityIcons).map((type) => {
                        return (
                          <MenuItem key={type} value={type}>
                            {amenityIcons[type]}
                            {getAmenityLabel(type)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  <TextField
                    label={t('space.details')}
                    size="small"
                    value={newAmenity.details}
                    onChange={(e) => setNewAmenity({ ...newAmenity, details: e.target.value })}
                    sx={{ width: { xs: '100%', md: 200 } }}
                  />

                  <AmountInput
                    amount={newAmenity.pricePerH.toString()}
                    label={t('space.pricePerHour')}
                    onAmountChange={(newAmount) => setNewAmenity({ ...newAmenity, pricePerH: parseFloat(newAmount) || 0 })}
                    onErrorChange={() => {}}
                    currencySymbol={currencySymbols[editValues?.currencyCode.toString() as keyof typeof currencySymbols] || '$'}
                    responsiveWidth={{ xs: '100%', md: 120 }}
                  />

                  <Tooltip title={t('space.addAmenity')} placement="left">
                    <IconButton color="primary" onClick={handleAddAmenity} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center', width: '100%' }}>
                    {amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={`${getAmenityLabel(amenity.amenityType)}${amenity.details ? ': ' + amenity.details : ''}${amenity.pricePerH ? ` - ${amenity.pricePerH.toFixed(2)} ${editValues?.currencyCode}` : ''}`}
                        variant="outlined"
                        size="small"
                        onDelete={() => handleDeleteAmenity(index)}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>

              {/* Rooms Section */}
              {!isIndividualTutorOrg ? (
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">{t('space.rooms')}</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/host/rooms/create?spaceId=${id}`)}
                  >
                    {t('space.addRoom')}
                  </Button>
                </Box>

                <RoomPublicationManager
                  ref={roomPublicationRef}
                  spaceId={id!}
                  rooms={rooms}
                  onRoomsChange={setRooms}
                  onInaccessible={() => navigate('/host/spaces')}
                  onDirtyChange={handlePublicationDirtyChange}
                  isMobile={isMobile}
                  onRoomClick={(roomId) => navigate(`/host/rooms/${roomId}`)}
                />
              </Stack>
              ) : null}
            </Stack>

          <Box display="flex" gap={2} mt={4} justifyContent="flex-end">
            <Button variant="contained" onClick={handleSave} disabled={!canSave}>
              {t('common.save')}
            </Button>
            <Button variant="outlined" onClick={handleCancel} disabled={!canSave}>
              {t('common.cancel')}
            </Button>
          </Box>
        </Card>
        
        <NotificationSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleSnackbarClose}
        />
      </Box>
    </>
  );
};

export default SpaceDetailsPage;
